"use client";

import { useRef, useState } from "react";
import type { ReactNode } from "react";
import { Button, CODE, GradText, SectionHead, SectionShell, Terminal, useBandRowAlign, useRefGridMode, useWholeRowBlocks } from "./kit";
import { gsap, insure, NOT_REDUCED, useEnterStagger, useGSAP } from "./kit/motion";
import { docsPath } from "@/components/docs/data/nav";

type Mode = "json" | "redirect" | "html";

const POINTS = (accent: string) => [
  {
    n: "01",
    title: "Routed per request",
    body: (
      <>
        Set up a query-parameter rule — say{" "}
        <span className={`font-jetbrains ${accent}`}>?format=json</span>{" "}
        — and the same link redirects browsers but returns your JSON to callers that use it.
      </>
    ),
  },
  {
    n: "02",
    title: "Edit without redeploying",
    body: "Flip a feature flag or repoint a deep link from the dashboard. Your app reads it instantly.",
  },
  {
    n: "03",
    title: "Any content type, any status",
    body: "Serve JSON, HTML, XML or plain text with the status you choose — 200, 404, 410 and more.",
  },
];

const REQUESTS: Record<Mode, ReactNode> = {
  json: (
    <>
      <span className="font-semibold text-[#7fdca0]">$</span> GET rii.link/sale
      <span className="text-[#e8e8f0]">?format=json</span>
    </>
  ),
  redirect: (
    <>
      <span className="font-semibold text-[#7fdca0]">$</span> GET rii.link/sale
      <span className="text-[#e8e8f0]"> -A &quot;Mozilla/5.0 (iPhone)&quot;</span>
    </>
  ),
  html: (
    <>
      <span className="font-semibold text-[#7fdca0]">$</span> GET rii.link/sale
      <span className="text-[#e8e8f0]">?format=html</span>
    </>
  ),
};

const RESPONSES: Record<Mode, ReactNode> = {
  json: (
    <>
      <span className={CODE.comment}>{"// the JSON body you wrote — no redirect"}</span>
      {"\n{\n  "}
      <span className={CODE.key}>&quot;sale&quot;</span>: <span className={CODE.string}>&quot;Summer Sale 2026&quot;</span>,{"\n  "}
      <span className={CODE.key}>&quot;destination&quot;</span>: <span className={CODE.string}>&quot;https://myshop.com&quot;</span>,{"\n  "}
      <span className={CODE.key}>&quot;apps&quot;</span>: {"{\n    "}
      <span className={CODE.key}>&quot;ios&quot;</span>:{"     "}<span className={CODE.string}>&quot;apps.apple.com/app/id123&quot;</span>,{"\n    "}
      <span className={CODE.key}>&quot;android&quot;</span>: <span className={CODE.string}>&quot;play.google.com/store/id123&quot;</span>,{"\n    "}
      <span className={CODE.key}>&quot;default&quot;</span>: <span className={CODE.string}>&quot;myshop.com/web&quot;</span>{"\n  },\n  "}
      <span className={CODE.key}>&quot;offer&quot;</span>: {"{\n    "}
      <span className={CODE.key}>&quot;discount&quot;</span>: <span className={CODE.number}>40</span>,{"\n    "}
      <span className={CODE.key}>&quot;code&quot;</span>: <span className={CODE.string}>&quot;SUMMER40&quot;</span>,{"\n    "}
      <span className={CODE.key}>&quot;in_stock&quot;</span>: <span className={CODE.bool}>true</span>{"\n  }\n}"}
    </>
  ),
  redirect: (
    <>
      <span className={CODE.comment}>{"// iPhone, no ?format → device-routed redirect"}</span>
      {"\n"}
      <span className="text-[#9b9bad]">{"< HTTP/1.1 "}</span>
      <span className={CODE.bool}>302 Found</span>
      {"\n"}
      <span className="text-[#9b9bad]">{"< Location: "}</span>
      <span className={CODE.string}>https://apps.apple.com/app/id123</span>
    </>
  ),
  html: (
    <>
      <span className={CODE.comment}>{"// ?format=html → the page you wrote"}</span>
      {"\n"}
      <span className="text-[#9b9bad]">{"< HTTP/1.1 "}</span>
      <span className={CODE.bool}>200 OK</span>
      {"\n"}
      <span className="text-[#9b9bad]">{"< Content-Type: text/html\n\n"}</span>
      <span className={CODE.key}>{"<!doctype html>"}</span>
      {"\n"}
      <span className={CODE.key}>{"<title>"}</span>Summer Sale 2026<span className={CODE.key}>{"</title>"}</span>
      {"\n"}
      <span className={CODE.key}>{'<meta property="og:image" '}</span>...
    </>
  ),
};

const MODES: { id: Mode; label: string }[] = [
  { id: "json", label: "JSON" },
  { id: "redirect", label: "30x" },
  { id: "html", label: "HTML" },
];

export default function LlmShowcaseSection() {
  const [mode, setMode] = useState<Mode>("json");
  // Light beat in the homepage's light/dark alternation — the dark terminal
  // chrome is this section's own anchor.
  const accent = "text-brand-600";
  const points = POINTS(accent);

  const bodyRef = useRef<HTMLDivElement>(null);
  const nullRef = useRef<HTMLDivElement>(null); // never attached — disables the hooks off-grid
  const onGrid = useRefGridMode();
  const termRef = useRef<HTMLDivElement>(null);
  const prevMode = useRef(mode);

  // useWholeRowBlocks/useBandRowAlign pad and re-align [data-grid-block]s for
  // grid-paper layout — running them on /developers (non-grid RefSections)
  // would inject inline padding/margins into this SHARED section and break
  // "visually unchanged". Hooks can't be called conditionally, so gate them by
  // ref instead: both bail immediately once `innerRef.current` is null (their
  // layout() starts from `innerRef.current?.closest("section")`), so pointing
  // them at `nullRef` (never attached to any element) off-grid is a full
  // no-op rather than a skipped hook call. Default breathing row: the body is
  // a text column + terminal, not one enclosing opaque card.
  useWholeRowBlocks(onGrid ? bodyRef : nullRef);
  useBandRowAlign(onGrid ? bodyRef : nullRef);

  // Numbered points rise in sequence as the section enters.
  useEnterStagger(bodyRef, "[data-llm-point]", { y: 20, stagger: 0.12 });

  // Terminal "prints": the request line types in (stepped clip) and the
  // response wipes down like scanlines.
  const print = () => {
    const tl = gsap
      .timeline({ defaults: { overwrite: "auto" } })
      .fromTo(
        "[data-t-request]",
        { clipPath: "inset(-2px 100% -2px 0)" },
        { clipPath: "inset(-2px 0% -2px 0)", duration: 0.45, ease: "steps(18)" },
      )
      .fromTo(
        "[data-t-response]",
        { clipPath: "inset(-2px -2px 100% -2px)" },
        { clipPath: "inset(-2px -2px -2px -2px)", duration: 0.65, ease: "steps(12)" },
        ">-0.05",
      );
    insure(tl);
    return tl;
  };

  // First scroll-enter plays the print once (IntersectionObserver — see
  // playOnEnter in kit/motion.ts for why not ScrollTrigger). If it never
  // fires, the terminal simply shows its content unanimated.
  useGSAP(
    (_, contextSafe) => {
      const mm = gsap.matchMedia();
      mm.add(NOT_REDUCED, () => {
        const el = termRef.current;
        if (!el) return;
        const io = new IntersectionObserver(
          (entries) => {
            if (entries.some((e) => e.isIntersecting)) {
              io.disconnect();
              contextSafe!(print)();
            }
          },
          { rootMargin: "0px 0px -15% 0px" },
        );
        io.observe(el);
        return () => io.disconnect();
      });
    },
    { scope: termRef },
  );

  // Every mode switch re-prints (skip the initial mount).
  useGSAP(
    (_, contextSafe) => {
      if (prevMode.current === mode) return;
      prevMode.current = mode;
      const mm = gsap.matchMedia();
      mm.add(NOT_REDUCED, () => {
        contextSafe!(print)();
      });
    },
    { dependencies: [mode], scope: termRef },
  );

  const toggle = (
    <span className="flex gap-1 rounded-lg bg-[#0d0d12] p-[3px]">
      {MODES.map((m) => (
        <button
          key={m.id}
          type="button"
          onClick={() => setMode(m.id)}
          className={`rounded-md px-2.5 py-1 font-jetbrains text-xs transition ${
            mode === m.id ? "bg-[#26262f] text-white" : "text-[#9b9bad]"
          }`}
        >
          {m.label}
        </button>
      ))}
    </span>
  );

  return (
    <SectionShell divider>
      <SectionHead
        center
        eyebrow="No redirect required"
        title={
          <>
            Your link is now an <GradText>endpoint.</GradText>
          </>
        }
        lead="On Premium, a short link can return the JSON, HTML or XML you write. Built for app configs, deep linking, and agents that need structured data — not a 30x bounce."
      />

      <div
        ref={bodyRef}
        data-grid-block
        className="grid items-center gap-12 lg:grid-cols-[1fr_1.15fr]"
      >
        <div>
          {points.map((pt, i) => (
            <div
              key={pt.n}
              data-llm-point
              className={`flex gap-3.5 py-[18px] ${i < points.length - 1 ? "border-b border-line" : ""}`}
            >
              <span className={`pt-0.5 font-jetbrains text-[13px] font-semibold ${accent}`}>
                {pt.n}
              </span>
              <div>
                <h4 className="mb-1.5 text-[16.5px] font-semibold">{pt.title}</h4>
                <p className="text-[14.5px] text-muted">{pt.body}</p>
              </div>
            </div>
          ))}
          <Button
            href={docsPath("api-links")}
            className="mt-6"
            iconRight={
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4}>
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            }
          >
            Read the API docs
          </Button>
        </div>

        <div ref={termRef}>
          <Terminal path="~/rii — curl" toggle={toggle} minHeight={340}>
            <div data-t-request className="mb-3.5 text-[#9b9bad]">{REQUESTS[mode]}</div>
            <pre data-t-response className="m-0 whitespace-pre-wrap font-[inherit]">{RESPONSES[mode]}</pre>
          </Terminal>
        </div>
      </div>
    </SectionShell>
  );
}
